import { Link } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { fmtEUR, withVAT } from "@/lib/api";
import { Trash2, ShoppingBag, Minus, Plus } from "lucide-react";

export default function Cart() {
    const { items, update, remove, subtotal, vatAmount, totalWithVat, count } = useCart();

    if (items.length === 0) {
        return (
            <div className="container-edge py-24 text-center" data-testid="cart-empty">
                <ShoppingBag className="mx-auto text-ink/40" size={48} />
                <h1 className="font-display font-black text-3xl mt-4">O seu carrinho está vazio</h1>
                <p className="text-ink/60 mt-2">
                    Comece a explorar o nosso catálogo e adicione produtos ao carrinho.
                </p>
                <Link to="/loja" className="btn-primary mt-6">
                    Ver Catálogo
                </Link>
            </div>
        );
    }

    return (
        <div className="container-edge py-12" data-testid="cart-page">
            <div className="label-mono text-magenta">Carrinho</div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2 mb-10">
                {count} {count === 1 ? "artigo" : "artigos"}
            </h1>

            <div className="grid lg:grid-cols-[1fr_380px] gap-8">
                <div className="border-2 border-ink bg-white">
                    {items.map((it, i) => (
                        <div
                            key={it.product_id}
                            className={`flex gap-4 p-5 ${i < items.length - 1 ? "border-b-2 border-ink" : ""}`}
                            data-testid={`cart-item-${it.sku}`}
                        >
                            <Link
                                to={`/produto/${it.product_id}`}
                                className="w-24 h-24 bg-muted border border-ink/30 shrink-0 overflow-hidden"
                            >
                                {it.image_url && (
                                    <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                                )}
                            </Link>
                            <div className="flex-1">
                                <Link
                                    to={`/produto/${it.product_id}`}
                                    className="font-display font-bold hover:text-magenta line-clamp-2"
                                >
                                    {it.name}
                                </Link>
                                <div className="text-xs font-mono text-ink/60 mt-1">SKU: {it.sku}</div>
                                <div className="mt-3 flex items-center gap-3">
                                    <div className="flex items-center border-2 border-ink">
                                        <button
                                            onClick={() => update(it.product_id, it.quantity - 1)}
                                            className="px-2 py-1 hover:bg-ink hover:text-white"
                                        >
                                            <Minus size={14} />
                                        </button>
                                        <span className="font-mono px-3">{it.quantity}</span>
                                        <button
                                            onClick={() =>
                                                update(it.product_id, Math.min(it.stock || 99, it.quantity + 1))
                                            }
                                            className="px-2 py-1 hover:bg-ink hover:text-white"
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => remove(it.product_id)}
                                        className="text-xs font-mono text-ink/60 hover:text-destructive flex items-center gap-1"
                                        data-testid={`remove-${it.sku}`}
                                    >
                                        <Trash2 size={14} /> Remover
                                    </button>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="font-mono text-xs text-ink/60">
                                    {fmtEUR(it.price)} s/IVA
                                    {it.campaign_name && (
                                        <span className="block text-magenta">{it.campaign_name}</span>
                                    )}
                                </div>
                                <div className="font-display font-black text-xl mt-1">
                                    {fmtEUR(withVAT(it.price * it.quantity, it.vat_rate ?? 23))}
                                </div>
                                <div className="text-[10px] font-mono text-ink/50">
                                    {fmtEUR(it.price * it.quantity)} s/IVA
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <aside className="border-2 border-ink bg-white p-6 h-fit lg:sticky lg:top-32" data-testid="cart-summary">
                    <h2 className="font-display font-black text-xl mb-5 border-b-2 border-ink pb-3">
                        Resumo
                    </h2>
                    <div className="space-y-3 font-mono text-sm">
                        <div className="flex justify-between" data-testid="cart-subtotal">
                            <span>Subtotal s/IVA</span>
                            <span>{fmtEUR(subtotal)}</span>
                        </div>
                        <div className="flex justify-between" data-testid="cart-vat">
                            <span>IVA ({items[0]?.vat_rate ?? 23}%)</span>
                            <span>{fmtEUR(vatAmount)}</span>
                        </div>
                        <div className="flex justify-between text-ink/60">
                            <span>Portes</span>
                            <span>Calculados no checkout</span>
                        </div>
                    </div>
                    <div className="mt-5 pt-4 border-t-2 border-ink flex justify-between items-end">
                        <span className="label-mono">Total c/IVA</span>
                        <span className="font-display font-black text-2xl text-magenta" data-testid="cart-total">{fmtEUR(totalWithVat)}</span>
                    </div>
                    <Link to="/checkout" className="btn-primary w-full mt-6" data-testid="checkout-btn">
                        Finalizar Compra
                    </Link>
                    <Link to="/loja" className="btn-ghost text-xs mt-4 inline-block">
                        ← Continuar a comprar
                    </Link>
                </aside>
            </div>
        </div>
    );
}
